import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { Link as RouterLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { SUPPORT_FRENCH } from "../../config/supportFrench";
import { SITE_BG, SURFACE } from "../../dark-theme/palette";
import { trackEvent } from "../../services/GoogleAnalytics";
import { usePagesTranslation } from "../locales";
import HowToPuzzleCrossword from "./puzzle-crossword/HowToCrossword";
import HowToPuzzleHiddenMessage from "./puzzle-hidden-message/HowToWordGrid";
import HowToPuzzleWordSearch from "./puzzle-word-search/HowToWordGrid";
import HowToSliding from "./puzzle-sliding/HowToSliding";
import HowToJigsaw from "./puzzle-jigsaw/HowToJigsaw";
import HowToWordWheel from "./puzzle-word-wheel/HowToWordWheel";
import HowToWordFormat from "./details/HowToWordFormat";
import HowToTranslate from "./details/HowToTranslate";
import HowToGridCoordinates from "./details/HowToGridCoordinates";
import HowToFrenchNormalization from "./details/HowToFrenchNormalization";
import HowToDisabledPuzzle from "./details/HowToDisabledPuzzle";
import HowToBannedWords from "./details/HowToBannedWords";
import HowToTips from "./HowToTips";
import HowToAssistant from "./HowToAssistant";
import HowToAppLogin from "./HowToAppLogin";

/** Scrollable tabs: labels (esp. “Hidden Message, Word Search”) may not fit in one row on phones; fullWidth clips overflow. */
const tabSx = {
    width: "100%",
    maxWidth: { xs: "100%", sm: "900px" },
    "& .MuiTabs-scrollButtons.Mui-disabled": {
        opacity: 0.3
    },
    "& .MuiTabs-indicator": {
        background: "linear-gradient(90deg, #2761F5 0%, #3BA666 100%)",
        height: 3
    },
    "& .MuiTab-root": {
        flexShrink: 0,
        minWidth: { xs: "7.5rem", sm: "9rem" },
        maxWidth: { xs: "11rem", sm: "none" },
        fontSize: { xs: "0.7rem", sm: "0.875rem", md: "1rem" },
        px: { xs: 1, sm: 1.5, md: 2 },
        py: 1,
        textTransform: "none",
        fontWeight: 500,
        whiteSpace: "normal",
        lineHeight: 1.2,
        "&.Mui-selected": {
            background: "linear-gradient(135deg, #2761F5 0%, #3BA666 100%)",
            color: "#fff",
            "&:hover": {
                background: "linear-gradient(135deg, #2761F5 0%, #4DAC71 100%)",
            }
        }
    }
};

const PUZZLE_HOWTO_PREFIX = "/howto/puzzle/";

function HowToTabsWithContent({ value, children, showDetailsSubMenu }) {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const showPuzzleTypeSubMenu = location.pathname.startsWith(PUZZLE_HOWTO_PREFIX);
    const puzzleHowtoLinks = [
        { to: `${PUZZLE_HOWTO_PREFIX}jigsaw`, labelKey: "stories.type-jigsaw" },
        { to: `${PUZZLE_HOWTO_PREFIX}sliding`, labelKey: "stories.type-sliding" },
        { to: `${PUZZLE_HOWTO_PREFIX}word-wheel`, labelKey: "stories.type-word-wheel" },
        { to: `${PUZZLE_HOWTO_PREFIX}crossword`, labelKey: "stories.type-crossword" },
        { to: `${PUZZLE_HOWTO_PREFIX}hidden-message`, labelKey: "stories.type-hidden-message" },
        { to: `${PUZZLE_HOWTO_PREFIX}word-search`, labelKey: "stories.type-word-search" },
    ];
    const handleTabChange = (_event, newValue) => {
        if (newValue === 0) navigate(`${PUZZLE_HOWTO_PREFIX}jigsaw`);
        else if (newValue === 1) navigate("/howto/assistant");
        else if (newValue === 2) navigate("/howto/tips");
        else navigate("/howto/hyphens");
    };
    return (
        <>
            <Box sx={{
                borderBottom: 2,
                borderColor: "#3BA666",
                backgroundColor: SURFACE.chrome,
                px: { xs: 1, sm: 2, md: 3 },
                pt: 2,
                display: "flex",
                justifyContent: "center"
            }}>
                <Tabs
                    value={value}
                    onChange={handleTabChange}
                    aria-label="how to play puzzle tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={tabSx}
                >
                    <Tab label={t("howto.tab-puzzles")} />
                    <Tab label={t("howto.tab-assistant")} />
                    <Tab label={t("howto.tab-tips")} />
                    <Tab label={t("howto.tab-details")} />
                </Tabs>
            </Box>
            {showPuzzleTypeSubMenu && (
                <Box sx={{ display: "flex", gap: 2, py: 1.5, flexWrap: "wrap", justifyContent: "center", backgroundColor: SURFACE.chrome, px: { xs: 1, sm: 2, md: 3 }, borderBottom: 1, borderColor: "divider" }}>
                    {puzzleHowtoLinks.map(({ to, labelKey }) => (
                        <Link
                            key={to}
                            component={RouterLink}
                            to={to}
                            underline="hover"
                            sx={{ fontSize: "0.875rem", color: "#2761F5", fontWeight: location.pathname === to ? 700 : 500 }}
                        >
                            {t(labelKey)}
                        </Link>
                    ))}
                </Box>
            )}
            {showDetailsSubMenu && (
                <Box sx={{ display: "flex", gap: 2, py: 1.5, flexWrap: "wrap", justifyContent: "center", backgroundColor: SURFACE.chrome, px: { xs: 1, sm: 2, md: 3 } }}>
                    <Link
                        component={RouterLink}
                        to="/howto/translate"
                        underline="hover"
                        sx={{ fontSize: "0.875rem", color: "#2761F5", fontWeight: 500 }}
                    >
                        {t("howto.link-translate")}
                    </Link>
                    <Link
                        component={RouterLink}
                        to="/howto/grid-coordinates"
                        underline="hover"
                        sx={{ fontSize: "0.875rem", color: "#2761F5", fontWeight: 500 }}
                    >
                        {t("howto.link-grid-coordinates")}
                    </Link>
                    <Link
                        component={RouterLink}
                        to="/howto/hyphens"
                        underline="hover"
                        sx={{ fontSize: "0.875rem", color: "#2761F5", fontWeight: 500 }}
                    >
                        {t("howto.link-hyphens")}
                    </Link>
                    {SUPPORT_FRENCH ? (
                    <Link
                        component={RouterLink}
                        to="/howto/french-normalization"
                        underline="hover"
                        sx={{ fontSize: "0.875rem", color: "#2761F5", fontWeight: 500 }}
                    >
                        {t("howto.link-french-norm")}
                    </Link>
                    ) : null}
                    <Link
                        component={RouterLink}
                        to="/howto/disabled-puzzle"
                        underline="hover"
                        sx={{ fontSize: "0.875rem", color: "#2761F5", fontWeight: 500 }}
                    >
                        {t("howto.link-disabled")}
                    </Link>
                    <Link
                        component={RouterLink}
                        to="/howto/banned-words"
                        underline="hover"
                        sx={{ fontSize: "0.875rem", color: "#2761F5", fontWeight: 500 }}
                    >
                        {t("howto.link-quality-safety")}
                    </Link>
                </Box>
            )}
            {children}
        </>
    );
}

export default function HowToMain() {
    const location = useLocation();

    useEffect(() => {
        trackEvent('entered', { name: 'HowToMain' });
    }, []);

    // Tab 0 = Puzzle how-to, 1 = Assistant, 2 = Tips, 3 = Details
    const detailsPaths = ["/howto/translate", "/howto/grid-coordinates", "/howto/hyphens", "/howto/disabled-puzzle", "/howto/banned-words"];
    if (SUPPORT_FRENCH) detailsPaths.push("/howto/french-normalization");
    const isDetailsPath = detailsPaths.includes(location.pathname);
    const isTipsPath = location.pathname === "/howto/tips";
    const isAssistantPath = location.pathname === "/howto/assistant";
    const isPuzzleHowtoPath = location.pathname.startsWith(PUZZLE_HOWTO_PREFIX);
    const tabValue = isPuzzleHowtoPath
        ? 0
        : isAssistantPath
            ? 1
            : isTipsPath
                ? 2
                : isDetailsPath
                    ? 3
                    : 0;

    return (
        <Box sx={{ width: "100%", backgroundColor: SITE_BG, minHeight: "100vh" }}>
            <Routes>
                <Route index element={<Navigate to={`${PUZZLE_HOWTO_PREFIX}jigsaw`} replace />} />
                <Route path="app-login" element={<HowToAppLogin />} />
                <Route path="assistant" element={
                    <HowToTabsWithContent value={tabValue} showDetailsSubMenu={false}>
                        <HowToAssistant />
                    </HowToTabsWithContent>
                } />
                <Route path="story/crossword" element={<Navigate to={`${PUZZLE_HOWTO_PREFIX}crossword`} replace />} />
                <Route path="story/hidden-message" element={<Navigate to={`${PUZZLE_HOWTO_PREFIX}hidden-message`} replace />} />
                <Route path="story/word-search" element={<Navigate to={`${PUZZLE_HOWTO_PREFIX}word-search`} replace />} />
                <Route path="puzzle/crossword" element={
                    <HowToTabsWithContent value={tabValue} showDetailsSubMenu={false}>
                        <HowToPuzzleCrossword />
                    </HowToTabsWithContent>
                } />
                <Route path="puzzle/hidden-message" element={
                    <HowToTabsWithContent value={tabValue} showDetailsSubMenu={false}>
                        <HowToPuzzleHiddenMessage />
                    </HowToTabsWithContent>
                } />
                <Route path="puzzle/word-search" element={
                    <HowToTabsWithContent value={tabValue} showDetailsSubMenu={false}>
                        <HowToPuzzleWordSearch />
                    </HowToTabsWithContent>
                } />
                <Route path="puzzle/sliding" element={
                    <HowToTabsWithContent value={tabValue} showDetailsSubMenu={false}>
                        <HowToSliding />
                    </HowToTabsWithContent>
                } />
                <Route path="puzzle/jigsaw" element={
                    <HowToTabsWithContent value={tabValue} showDetailsSubMenu={false}>
                        <HowToJigsaw />
                    </HowToTabsWithContent>
                } />
                <Route path="puzzle/word-wheel" element={
                    <HowToTabsWithContent value={tabValue} showDetailsSubMenu={false}>
                        <HowToWordWheel />
                    </HowToTabsWithContent>
                } />
                <Route path="crossword" element={<Navigate to={`${PUZZLE_HOWTO_PREFIX}crossword`} replace />} />
                <Route path="word-search" element={<Navigate to={`${PUZZLE_HOWTO_PREFIX}word-search`} replace />} />
                <Route path="tips" element={
                    <HowToTabsWithContent value={tabValue} showDetailsSubMenu={false}>
                        <HowToTips />
                    </HowToTabsWithContent>
                } />
                <Route path="word-directions" element={<Navigate to={{ pathname: `${PUZZLE_HOWTO_PREFIX}crossword`, hash: "word-directions" }} replace />} />
                <Route path="translate" element={
                    <HowToTabsWithContent value={tabValue} showDetailsSubMenu>
                        <HowToTranslate />
                    </HowToTabsWithContent>
                } />
                <Route path="grid-coordinates" element={
                    <HowToTabsWithContent value={tabValue} showDetailsSubMenu>
                        <HowToGridCoordinates />
                    </HowToTabsWithContent>
                } />
                <Route path="hyphens" element={
                    <HowToTabsWithContent value={tabValue} showDetailsSubMenu>
                        <HowToWordFormat />
                    </HowToTabsWithContent>
                } />
                <Route
                    path="french-normalization"
                    element={
                        SUPPORT_FRENCH ? (
                            <HowToTabsWithContent value={tabValue} showDetailsSubMenu>
                                <HowToFrenchNormalization />
                            </HowToTabsWithContent>
                        ) : (
                            <Navigate to="/howto/tips" replace />
                        )
                    }
                />
                <Route path="disabled-puzzle" element={
                    <HowToTabsWithContent value={tabValue} showDetailsSubMenu>
                        <HowToDisabledPuzzle />
                    </HowToTabsWithContent>
                } />
                <Route path="banned-words" element={
                    <HowToTabsWithContent value={tabValue} showDetailsSubMenu>
                        <HowToBannedWords />
                    </HowToTabsWithContent>
                } />
                <Route path="install" element={<Navigate to="/howto/assistant" replace />} />
            </Routes>
        </Box>
    );
}

